// react
import React from 'react';


// third-party
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

// application
import Footer from './footer';
import Header from './header';
import MobileHeader from './mobile/MobileHeader';
import MobileMenu from './mobile/MobileMenu';
import Quickview from './shared/Quickview';

// pages
import AccountLayout from './account/AccountLayout';
import AccountPageLogin from './account/AccountPageLogin';
import BlogPageCategory from './blog/BlogPageCategory';
import SitePageAboutUs from './site/SitePageAboutUs';
import PageCart from './shop/ShopPageCart';
import PageCheckout from './shop/ShopPageCheckout';
import PageCompare from './shop/ShopPageCompare';
import SitePageComponents from './site/SitePageComponents';
import SitePageContactUs from './site/SitePageContactUs';
import SitePageContactUsAlt from './site/SitePageContactUsAlt';
import SitePageFaq from './site/SitePageFaq';
import SitePageNotFound from './site/SitePageNotFound';
import BlogPagePost from './blog/BlogPagePost';
import ShopPageProduct from './shop/ShopPageProduct';
import SitePageTerms from './site/SitePageTerms';
import ShopPageTrackOrder from './shop/ShopPageTrackOrder';
import SitePageTypography from './site/SitePageTypography';
import SearchResults from './searchResults/SearchResults';
import PageWishlist from './shop/ShopPageWishlist';
import ShopPageCategory from './shop/ShopPageCategory';
import ThankYou from './site/ThankYou';


// data stubs
import theme from '../data/theme';

function getQueryVariable(variable)
{
        var query = window.location.search.substring(1);
        var vars = query.split("&");
        
        for (var i=0;i<vars.length;i++) {
            var pair = vars[i].split("=");        
            if(pair[0] === variable){return decodeURI(pair[1]);}
        }
        return(false);
}

function getOpenGraphPageType()
{
    var og = "page.generic";
        
    if (window.location.pathname.indexOf("/shop/product") === 0) {
        og = "page.product";
    } else if (window.location.pathname.indexOf("/shop/category") === 0) {
        og = "page.category";
    } else if (window.location.pathname.indexOf("/shop/cart") === 0) {
        og = "page.cart";
    } else if (window.location.pathname.indexOf("/shop/checkout") === 0) {
        og = "page.checkout";
    } else if (window.location.pathname.length <= 1) {
        og = "page.front";
    }
    
    return og;

}

function Layout({ headerLayout, homeComponent: HomeComponent }) {
    const productCategory = getQueryVariable("cat");
    const productSubCategory = getQueryVariable("subcat");
    const ogType = getOpenGraphPageType();
    console.log("og page type: " + ogType);
    console.log("Category: " + getQueryVariable("cat"));
    console.log("Sub-Category: " + getQueryVariable("subcat"));

    return (
        <React.Fragment>
            <Helmet>
                <title>{theme.name}</title>
                <meta name="description" content={theme.fullName} />
                <meta property="og:type" content={ogType} />
            </Helmet>

            <ToastContainer autoClose={5000} hideProgressBar />

            <Quickview />

            <MobileMenu />

            <div className="site">
                <header className="site__header d-lg-none">
                    <MobileHeader />
                </header>

                <header className="site__header d-lg-block d-none">
                    <Header layout={headerLayout} />
                </header>

                <div className="site__body">
                    <Routes>
                        {/*
                        // Home
                        */}
                        <Route index element={<HomeComponent />} />
                        {/*
                        // Shop
                        */}
                        <Route path="shop" element={<Navigate to="/shop/category-grid-3-columns-sidebar" replace />} />

                        <Route
                            path="shop/category-grid-3-columns-sidebar"
                            element={(
                                <ShopPageCategory columns={3} viewMode="grid" sidebarPosition="start" productCategory={productCategory} productSubCategory={productSubCategory} />
                            )}
                        />
                        <Route
                            path="shop/category-grid-4-columns-full"
                            element={(
                                <ShopPageCategory columns={4} viewMode="grid" productCategory={productCategory} productSubCategory={productSubCategory} />
                            )}
                        />
                        <Route
                            path="shop/category-grid-5-columns-full"
                            element={(
                                <ShopPageCategory columns={5} viewMode="grid" productCategory={productCategory} productSubCategory={productSubCategory} />
                            )}
                        />
                        <Route
                            path="shop/category-list"
                            element={(
                                <ShopPageCategory columns={3} viewMode="list" sidebarPosition="start" productCategory={productCategory} productSubCategory={productSubCategory} />
                            )}
                        />
                        <Route
                            path="shop/category-right-sidebar"
                            element={(
                                <ShopPageCategory columns={3} viewMode="grid" sidebarPosition="end" productCategory={productCategory} productSubCategory={productSubCategory} />
                            )}
                        />

                        <Route path="shop/product/:productId" element={<ShopPageProduct />} />
                        <Route
                            path="shop/product-standard"
                            element={<ShopPageProduct layout="standard" />}
                        />
                        <Route
                            path="shop/product-columnar"
                            element={<ShopPageProduct layout="columnar" />}
                        />
                        <Route
                            path="shop/product-sidebar"
                            element={<ShopPageProduct layout="sidebar" />}
                        />

                        <Route path="shop/cart" element={<PageCart />} />
                        <Route path="shop/checkout" element={<PageCheckout />} />
                        <Route path="shop/wishlist" element={<PageWishlist />} />
                        <Route path="shop/compare" element={<PageCompare />} />
                        <Route path="shop/track-order" element={<ShopPageTrackOrder />} />

                        {/*
                        // Blog
                        */}
                        <Route path="blog" element={<Navigate to="/blog/category-classic" replace />} />
                        <Route
                            path="blog/category-classic"
                            element={<BlogPageCategory layout="classic" sidebarPosition="end" />}
                        />
                        <Route
                            path="blog/category-grid"
                            element={<BlogPageCategory layout="grid" sidebarPosition="end" />}
                        />
                        <Route
                            path="blog/category-list"
                            element={<BlogPageCategory layout="list" sidebarPosition="end" />}
                        />
                        <Route
                            path="blog/category-left-sidebar"
                            element={<BlogPageCategory layout="classic" sidebarPosition="start" />}
                        />
                        <Route
                            path="blog/post-classic"
                            element={<BlogPagePost layout="classic" sidebarPosition="end" />}
                        />
                        <Route
                            path="blog/post-full"
                            element={<BlogPagePost layout="full" />}
                        />

                        {/*
                        // Account
                        */}
                        <Route path="account/login" element={<AccountPageLogin />} />
                        <Route path="account/*" element={<AccountLayout />} />

                        {/*
                        // Site
                        */}
                        <Route path="site" element={<Navigate to="/site/about-us" replace />} />
                        <Route path="site/about-us" element={<SitePageAboutUs />} />
                        <Route path="site/components" element={<SitePageComponents />} />
                        <Route path="site/contact-us" element={<SitePageContactUs />} />
                        <Route path="site/contact-us-alt" element={<SitePageContactUsAlt />} />
                        <Route path="site/faq" element={<SitePageFaq />} />
                        <Route path="site/not-found" element={<SitePageNotFound />} />
                        <Route path="site/terms" element={<SitePageTerms />} />
                        <Route path="site/thankyou" element={<ThankYou />} />
                        <Route path="site/typography" element={<SitePageTypography />} />
                        <Route path="search" element={<SearchResults />} />

                        {/*
                        // Page Not Found
                        */}
                        <Route path="*" element={<SitePageNotFound />} />
                    </Routes>
                </div>

                <footer className="site__footer">
                    <Footer />
                </footer>
            </div>
        </React.Fragment>
    );
}

Layout.propTypes = {
    /**
     * header layout (default: 'classic')
     * one of ['classic', 'compact']
     */
    headerLayout: PropTypes.oneOf(['default', 'compact']),
    /**
     * home component
     */
    homeComponent: PropTypes.elementType.isRequired,
};

export default Layout;
